import React from 'react'
import {
    getValidLinkedSourceTypes,
    getValidSourceTypes,
    getValidUploadedSourceTypes,
} from '../../../../utils/issues/sourceTypes'
import Typography from '@mui/material/Typography'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select, { SelectProps } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormHelperText from '@mui/material/FormHelperText'
import { Controller, RegisterOptions, UseFormReturn } from 'react-hook-form'
import { intersection } from './utils'
import { SourceForm } from '../../types/SourceForm'
import { IssueType, SourceType, TransactionType } from '@finance-lab/utils/types'

const sourceTypes = {
    internalSource: getValidLinkedSourceTypes(),
    linkedSource: getValidLinkedSourceTypes(),
    uploadedSource: getValidUploadedSourceTypes(),
}

export function SourceTypeSelect({
    issueType,
    label,
    formMethods,
    validation,
    props,
    setOfTypes = 'linkedSource',
    transactionType,
    excludeSourceTypes,
}: {
    label: string
    formMethods: UseFormReturn<SourceForm>
    validation?: RegisterOptions<Pick<SourceForm, 'sourceType'>>
    props?: SelectProps
    setOfTypes?: 'linkedSource' | 'uploadedSource' | 'internalSource'
    transactionType: TransactionType | null
    issueType: IssueType | null
    excludeSourceTypes: SourceType[]
}) {
    const name = 'sourceType'
    let typesToList = transactionType
        ? intersection(sourceTypes[setOfTypes], getValidSourceTypes(transactionType, issueType))
        : sourceTypes[setOfTypes]

    if (excludeSourceTypes) {
        typesToList = typesToList.filter((type) => !excludeSourceTypes.includes(type))
    }

    if (!['linkedSource', 'uploadedSource', 'internalSource'].includes(setOfTypes)) {
        console.error('Invalid setOfTypes: ' + setOfTypes)
        return null
    }

    const error = formMethods.formState.errors[name]
    if (typesToList.length === 0) {
        return <Typography>Inga tillåtna källtyper</Typography>
    }
    if (typesToList.length === 1) {
        if (formMethods.getValues()[name] !== typesToList[0]) {
            formMethods.setValue(name, typesToList[0])
        }

        return <Typography>Källtyp: {typesToList[0]}</Typography>
    }

    return (
        <FormControl sx={{ minWidth: 150 }} error={!!error}>
            <InputLabel id={`${name}-source-type-select-label`}>{label}</InputLabel>
            <Controller
                name={name}
                control={formMethods.control}
                rules={validation}
                defaultValue={typesToList[0]}
                render={({ field }) => (
                    <Select
                        defaultValue={typesToList.length === 1 ? typesToList[0] : undefined}
                        labelId={`${name}-source-type-select-label`}
                        label={label}
                        sx={{ minWidth: 150 }}
                        {...field}
                        {...props}
                    >
                        <MenuItem value='' disabled>
                            Välj källtyp
                        </MenuItem>
                        {typesToList.map((type) => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            />
            {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
    )
}
